/**
 * The choices of a categories score, one per row: type, Enter for the next
 * row, × to drop one. Blank rows are ignored when the score is saved.
 */

import { useRef } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ChoiceList({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const rows = useRef<Array<HTMLInputElement | null>>([]);
  const add = (at: number) => {
    onChange([...value.slice(0, at), "", ...value.slice(at)]);
    // Focus once the new row is rendered.
    setTimeout(() => rows.current[at]?.focus());
  };

  return (
    <div className="flex flex-col gap-1.5">
      {value.map((choice, i) => (
        <div key={i} className="flex items-center gap-2">
          <span aria-hidden className="w-4 text-right font-mono text-[11px] text-faint">
            {i + 1}
          </span>
          <input
            ref={(el) => {
              rows.current[i] = el;
            }}
            className="inp min-w-0 flex-1"
            aria-label={`Choice ${i + 1}`}
            placeholder={i === 0 ? "e.g. Missed nodule" : i === 1 ? "e.g. Wrong size threshold" : ""}
            value={choice}
            onChange={(e) => onChange(value.map((c, j) => (j === i ? e.target.value : c)))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add(i + 1);
              }
            }}
          />
          <Button
            icon
            variant="ghost"
            aria-label={`Remove choice ${i + 1}`}
            disabled={value.length <= 2}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <X size={14} strokeWidth={2.2} />
          </Button>
        </div>
      ))}
      <Button size="sm" className="ml-6 self-start" onClick={() => add(value.length)}>
        <Plus size={13} strokeWidth={2.2} aria-hidden />
        Add choice
      </Button>
    </div>
  );
}
